<template lang='pug'>

div.nopadding
    div.fullwidth.video-container
        video.hero-video(ref="heroVideo", autoplay, loop, muted, playsinline, preload="none")
            source(src="@/assets/hero_av1_1080.webm", type='video/webm; codecs="av01.0.08M.08"')
            source(src="@/assets/hero_vb9_720.webm", type='video/webm; codecs="vp9"')
        div.fullwidth.welcome-box(v-if="$route.params.lang == 'en'")
            .welcome-content
                h1 Partial Discharge Monitoring
                p.welcome-subslogan STAY IN CHARGE


        div.fullwidth.welcome-box(v-if="$route.params.lang == 'de'")
            .welcome-content
                h1 Partial Discharge Monitoring
                p.welcome-subslogan STAY IN CHARGE

    .content-container.solution-content(v-if="$route.params.lang == 'en'")
        .columns.flex-equal
            div
                h2.secondary-header Welcome to BSS
                p.welcome-text At BSS, we develop and manufacture advanced partial discharge monitoring systems and sensors to assess the condition of electrical assets. Based in Stuttgart, Germany, we empower customers worldwide to stop reacting to faults and start predicting them by providing deep insights into the insulation condition of their assets.
                h2.secondary-header Partial Discharge
                p Partial discharge (PD) is a localized electrical breakdown that does not fully bridge the insulation between conductors. Occurring in voids, at sharp conductor edges, inside air bubbles, or along contaminated surfaces, PD slowly degrades the insulation inside the transformer. If left unchecked, this erosion can ultimately result in a complete system failure.


                h2.secondary-header Benefits of Online PD Monitoring

                ul.arrow-list
                    li Early warning of insulation degradation before catastrophic failure
                    li Detects issues from manufacturing defects, insulation material aging, moisture, or mechanical stress
                    li  Supports condition-based maintenance instead of fixed schedules


                div.cta.bigbox-link
                    Sitelink(to="/en/applications", text="Discover our PD monitoring solutions")
            div
                picture.solution-image
                    img( src='@/assets/svg/trafo.svg', width="100%")
                    
        h2.secondary-header Detection methods
        .card-grid
            MethodBox(
                title="Electrical",
                standard="(IEC 60270 – Charge-Based)",
                linkTo="/en/products/electricalpdsensors/overview",
                linkText="Electrical PD Sensors"
            )
                template(#icon)
                    svg(viewBox="0 0 24 24" width="24" height="24" fill="currentColor")
                        path(d="M13 2L3 14h9l-1 8 10-12h-9l1-8z")
                ul
                    li Measures apparent charge in picocoulombs (pC) via the bushing test tap.
                    li The established industry standard for Factory Acceptance Tests (FAT).
                    li  Calibration according to the IEC 60270 standard.


            MethodBox(
                title="UHF",
                standard="(Electromagnetic PD Measurement)",
                linkTo="/en/products/uhfsensors/overview",
                linkText="UHF PD Sensors"
            )
                template(#icon)
                    svg(viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
                        path(d="M4.93 4.93a10 10 0 0 1 14.14 0")
                        path(d="M7.76 7.76a6 6 0 0 1 8.48 0")
                        circle(cx="12" cy="12" r="2" fill="currentColor")
                        path(d="M12 14v7")
                        path(d="M9 21h6")
                ul
                    li Detects electromagnetic waves (300 MHz – 3 GHz) emitted by PD.
                    li Sensors are installed via oil drain valves or dedicated dielectric windows.
                    li Offers excellent noise immunity and high sensitivity, making it ideal for continuous online monitoring.


            MethodBox(
                title="Acoustic Emission",
                standard="(Ultrasonic PD Detection)",
                linkTo="/en/products/otherpdsensors/ae-150-m",
                linkText="Acoustic PD Sensors"
            )
                template(#icon)
                    svg(viewBox="-26 36 720 720" width="30" height="30" fill="currentColor")
                        path(d='M329.94 83.64C349.34 81.81 372.5 85.7 390.99 91.62C452.58 111.34 499.3 157.9 517.91 220.1C525.47 245.38 526.69 272.98 524.32 299.15C522.75 316.59 521.25 334.09 515.93 350.89C505.29 384.43 485.05 409.8 465.29 438.26C448.03 463.11 433.51 489.97 421.23 517.57C413.58 534.77 406.09 552.95 401.36 571.19C397.19 587.28 393.15 602.69 385.93 617.78C358.85 674.39 299.43 710.71 236.45 703.02C193.87 697.81 159.06 665.13 149.73 623.65C146.58 609.66 141.49 585.95 156.72 577.31C166.96 571.5 181.24 575.72 185.88 586.78C188.39 592.77 187.59 599.63 188.16 605.97C188.68 611.79 190.35 617.68 192.26 623.18C203.38 655.14 240.08 667.83 271.14 661.73C304.65 655.14 332.25 631.54 347.7 601.48C352.3 592.52 356.09 582.94 358.75 573.25C360.59 566.57 361.9 559.85 364.13 553.29C373.92 524.4 385.54 496.18 399.2 468.86C409.76 447.73 422.51 427.74 436.06 408.39C448.28 390.93 462.09 373.98 470.65 354.3C483.09 325.71 485.66 292.71 483.82 261.8C483.08 249.34 480.18 236.88 476.16 225.12C448.95 145.52 358.96 103.01 280.41 135.29C238.23 152.63 205.93 188.53 193.25 232.37C190.7 241.19 188.7 250.61 188.12 259.8C187.68 266.86 188.66 274.91 185.77 281.53C180.17 294.35 162.12 297.09 152.5 287.39C145.7 280.54 146.46 271.42 146.85 262.46C147.61 245.07 151.24 228.16 156.63 211.67C174.09 158.3 219.1 112.06 272.57 94.2C284.92 90.07 297.67 86.52 310.64 85C317.07 84.24 323.51 84.25 329.94 83.64ZM279.9 329.83C283.16 331.22 286.99 331.32 290.46 332.15C294.73 333.16 298.92 334.72 303 336.33C324.62 344.84 341.37 363.23 346.74 385.9C358.58 435.9 317.49 493.02 273.51 513.32C260.33 519.4 236.81 526.65 222.55 522.78C206.5 518.42 203.06 496.28 216.11 486.57C222.48 481.83 229.94 482.82 237.39 481.89C241.73 481.34 246.17 480.15 250.3 478.72C277.14 469.43 303.84 440.03 306.84 411.21C307.39 405.99 307.87 400.44 306.67 395.27C303.23 380.51 288.8 373.07 274.82 371.2C262.31 369.52 246.04 372.28 240.24 357.72C237.92 351.91 238.71 345.33 238.71 339.17C238.71 328.5 238.71 317.83 238.71 307.15C238.71 285.96 236.6 263.77 243.09 243.33C257.51 197.82 306.22 167.43 353.62 177.07C389.35 184.34 419.04 210.19 429.2 245.53C433.65 261.01 438.91 288.09 417.21 292.88C414.77 293.42 412.18 293.43 409.72 293.13C388.88 290.56 394.05 270.59 389.81 256.88C386.01 244.6 378.17 233.71 367.55 226.42C339.5 207.18 299.71 218.18 285.14 248.74C277.26 265.27 279.63 284.68 279.63 302.48C279.63 310.89 278.32 321.74 279.9 329.83Z' fill='currentColor' fill-rule='evenodd')
                ul
                    li Piezoelectric sensors externally mounted on the tank wall detect acoustic waves generated by PD.
                    li Completely non-intrusive approach requiring no electrical connection to the asset.
                    li  Good for localizing the PD source, though typically less sensitive compared to UHF or electrical methods. 



    div.fullwidth(v-if="$route.params.lang == 'en'")
        .content-container
            hgroup
                h2 Our Flagship PD Monitoring System: PDM-600
                p.subtitle Multi-Sensor Partial Discharge Monitoring System with Flexible Channel Configuration
            .columns
                picture.content-picture.bigbox-image
                    source(type='image/webp', srcset='@/assets/extras/PDM-600_GUI_v1.3.0.webp')
                    img( src='@/assets/extras/PDM-600_GUI_v1.3.0.png', width="100%", alt='PD Monitoring System PDM-600', title='PD Monitoring System PDM-600')
                div.bigbox-text
                    ul
                        li All-in-one solution for UHF, electrical (IEC&nbsp60270), acoustic and TEV PD monitoring
                        li Continuous, parallel measurement
                        li Can be configured with up to 6 UHF, electrical, acoustic or TEV PD channels
                        li Rich visualisation in built-in web GUI
                        li ModbusTCP, IEC&nbsp61850, HTTP API and relays built in
                    //- router-link.cta(to="/en/products/pdm-600") More Information
                    div.cta.bigbox-link
                        Sitelink(to="/en/products/pdm-600", text="More Information on the PDM-600")

        .content-container
            hgroup
                h2 Now Compatible with a Wider Range of Oil Valves: The Upgraded UHF-DVS-50
                p.subtitle UHF Drain Valve Sensor for Retrofit at the DN50 / DN80 Oil Valves of Transformers during Operation
            .columns
                picture.content-picture.bigbox-image
                    source(type='image/webp', srcset='@/assets/photos/UHF-DVS-50.webp')
                    img( src='@/assets/photos/UHF-DVS-50.png', width="100%", alt='UHF PD Sensor UHF DVS 50', title='UHF PD Sensor UHF DVS 50')
                div.bigbox-text
                    ul
                        li Now with fully retractable antenna
                        li For oil-filled transformers
                        li Insertion depth max. 410 mm
                        li Bandwidth: 100 MHz ... 3 GHz
                        li Retrofit possible at DN50 and DN80 valves
                        li Integrated high-pass filter
                    div.cta.bigbox-link
                        Sitelink(to="/en/products/uhfsensors/uhf-dvs-50", text="More Information on the UHF-DVS-50")

        .content-container(v-if='upcomingEvents.length')
            h2.secondary-header Upcoming Events
        Event(
            v-for='event in upcomingEvents'
            :key='event.image'
            :image='event.image'
            :website='event.website'
            contact-path='/en/contact'
            contact-text='Schedule a meeting'
            v-bind='event.en'
        )


    .content-container.solution-content(v-if="$route.params.lang == 'de'")
        .columns.flex-equal
            div
                h2.secondary-header Willkommen bei BSS
                p.welcome-text Bei BSS entwickeln und fertigen wir fortschrittliche TE-Monitoringsysteme und Sensoren zur Zustandsbewertung elektrischer Betriebsmittel. Von unserem Standort in Stuttgart aus unterstützen wir Kunden weltweit dabei, nicht mehr auf Störungen zu reagieren, sondern diese vorherzusagen – durch tiefgehende Einblicke in den Isolationszustand ihrer Betriebsmittel.
                h2.secondary-header Was sind Teilentladungen?

                p Eine Teilentladung (TE) ist eine lokale elektrische Entladung, die die Isolierung zwischen den Leitern nicht vollständig überbrückt. Sie tritt typischerweise in Hohlräumen, an scharfen Leiterkanten, in Gaseinschlüssen oder entlang verunreinigter Oberflächen auf und schädigt die Isolierung des Transformators schleichend. Bleibt sie unentdeckt, kann dieser Materialabbau letztendlich zu einem Totalausfall führen.



                h2.secondary-header Vorteile des Online-TE-Monitorings:


                ul.arrow-list
                    li
                        b Präventives Frühwarnsystem: 
                        span  Erkennt Isolationsschäden frühzeitig, lange bevor ein Totalausfall droht.
                    li
                        b Umfassende Fehlerdiagnose:
                        span  Identifiziert Probleme durch Fertigungsfehler, Materialalterung, Feuchtigkeit oder mechanische Beanspruchung.
                    li
                        b Zustandsbasierte Instandhaltung:
                        span  Ermöglicht bedarfsgerechte Wartungen (Condition-Based Maintenance) anstelle starrer, zeitbasierter Intervalle.


                div.cta.bigbox-link
                    Sitelink(to="/de/applications", text="Unsere TE-Monitoringlösungen entdecken")
            div
                picture.solution-image
                    img( src='@/assets/svg/trafo.svg', width="100%")
                    
        h2.secondary-header Detektionsmethoden
        .card-grid
            MethodBox(
                title="Elektrische TE-Messung",
                standard="(Ladungsbasiert nach IEC 60270)",
                linkTo="/de/products/electricalpdsensors/overview",
                linkText="Elektrische TE-Sensoren"
            )
                template(#icon)
                    svg(viewBox="0 0 24 24" width="24" height="24" fill="currentColor")
                        path(d="M13 2L3 14h9l-1 8 10-12h-9l1-8z")
                ul
                    li Misst die scheinbare Ladung in Picocoulomb (pC) am Messanschluss der Durchführungen.
                    li Der etablierte Industriestandard für Werksabnahmeprüfungen (FAT).
                    li Kalibrierung gemäß IEC 60270.

            MethodBox(
                title="UHF-TE-Messung",
                standard="(Elektromagnetisches Verfahren)",
                linkTo="/de/products/uhfsensors/overview",
                linkText="UHF-TE-Sensoren"
            )
                template(#icon)
                    svg(viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
                        path(d="M4.93 4.93a10 10 0 0 1 14.14 0")
                        path(d="M7.76 7.76a6 6 0 0 1 8.48 0")
                        circle(cx="12" cy="12" r="2" fill="currentColor")
                        path(d="M12 14v7")
                        path(d="M9 21h6")
                ul
                    li Erfasst die bei Teilentladungen entstehenden elektromagnetischen Wellen (300 MHz bis 3 GHz).
                    li Nutzt Sensoren, die über vorhandene Ölschieber oder spezielle dielektrische Fenster im Kessel installiert werden.
                    li Bietet eine hervorragende Störfestigkeit und hohe Empfindlichkeit – ideal für das kontinuierliche Online-Monitoring.


            MethodBox(
                title="Akustische TE-Messung",
                standard="(Ultraschall-Detektion)",
                linkTo="/de/products/otherpdsensors/ae-150-m",
                linkText="Akustische TE-Sensoren"
            )
                template(#icon)
                    svg(viewBox="-26 36 720 720" width="30" height="30" fill="currentColor")
                        path(d='M329.94 83.64C349.34 81.81 372.5 85.7 390.99 91.62C452.58 111.34 499.3 157.9 517.91 220.1C525.47 245.38 526.69 272.98 524.32 299.15C522.75 316.59 521.25 334.09 515.93 350.89C505.29 384.43 485.05 409.8 465.29 438.26C448.03 463.11 433.51 489.97 421.23 517.57C413.58 534.77 406.09 552.95 401.36 571.19C397.19 587.28 393.15 602.69 385.93 617.78C358.85 674.39 299.43 710.71 236.45 703.02C193.87 697.81 159.06 665.13 149.73 623.65C146.58 609.66 141.49 585.95 156.72 577.31C166.96 571.5 181.24 575.72 185.88 586.78C188.39 592.77 187.59 599.63 188.16 605.97C188.68 611.79 190.35 617.68 192.26 623.18C203.38 655.14 240.08 667.83 271.14 661.73C304.65 655.14 332.25 631.54 347.7 601.48C352.3 592.52 356.09 582.94 358.75 573.25C360.59 566.57 361.9 559.85 364.13 553.29C373.92 524.4 385.54 496.18 399.2 468.86C409.76 447.73 422.51 427.74 436.06 408.39C448.28 390.93 462.09 373.98 470.65 354.3C483.09 325.71 485.66 292.71 483.82 261.8C483.08 249.34 480.18 236.88 476.16 225.12C448.95 145.52 358.96 103.01 280.41 135.29C238.23 152.63 205.93 188.53 193.25 232.37C190.7 241.19 188.7 250.61 188.12 259.8C187.68 266.86 188.66 274.91 185.77 281.53C180.17 294.35 162.12 297.09 152.5 287.39C145.7 280.54 146.46 271.42 146.85 262.46C147.61 245.07 151.24 228.16 156.63 211.67C174.09 158.3 219.1 112.06 272.57 94.2C284.92 90.07 297.67 86.52 310.64 85C317.07 84.24 323.51 84.25 329.94 83.64ZM279.9 329.83C283.16 331.22 286.99 331.32 290.46 332.15C294.73 333.16 298.92 334.72 303 336.33C324.62 344.84 341.37 363.23 346.74 385.9C358.58 435.9 317.49 493.02 273.51 513.32C260.33 519.4 236.81 526.65 222.55 522.78C206.5 518.42 203.06 496.28 216.11 486.57C222.48 481.83 229.94 482.82 237.39 481.89C241.73 481.34 246.17 480.15 250.3 478.72C277.14 469.43 303.84 440.03 306.84 411.21C307.39 405.99 307.87 400.44 306.67 395.27C303.23 380.51 288.8 373.07 274.82 371.2C262.31 369.52 246.04 372.28 240.24 357.72C237.92 351.91 238.71 345.33 238.71 339.17C238.71 328.5 238.71 317.83 238.71 307.15C238.71 285.96 236.6 263.77 243.09 243.33C257.51 197.82 306.22 167.43 353.62 177.07C389.35 184.34 419.04 210.19 429.2 245.53C433.65 261.01 438.91 288.09 417.21 292.88C414.77 293.42 412.18 293.43 409.72 293.13C388.88 290.56 394.05 270.59 389.81 256.88C386.01 244.6 378.17 233.71 367.55 226.42C339.5 207.18 299.71 218.18 285.14 248.74C277.26 265.27 279.63 284.68 279.63 302.48C279.63 310.89 278.32 321.74 279.9 329.83Z' fill='currentColor' fill-rule='evenodd')
                ul
                    li Erfasst die von Teilentladungen erzeugten Schallwellen über piezoelektrische Sensoren an der Kesselaußenwand.
                    li Nicht-invasives Verfahren, das keinerlei elektrischen Eingriff am Betriebsmittel erfordert.
                    li Ermöglicht die Lokalisierung der TE-Quelle, ist jedoch weniger empfindlich als UHF- oder elektrische Verfahren.

    div.fullwidth(v-if="$route.params.lang == 'de'")
        .content-container
            hgroup
                h2 Unser Multisensor TE-Monitoring-System: PDM-600
                p.subtitle Teilentladungs-Monitoring-System mit flexibler Kanalkonfiguration
            .columns
                picture.content-picture.bigbox-image
                    source(type='image/webp', srcset='@/assets/extras/PDM-600_GUI_v1.3.0.webp')
                    img( src='@/assets/extras/PDM-600_GUI_v1.3.0.png', width="100%", alt='TE-Monitoring System PDM-600', title='TE-Monitoring System PDM-600')
                div.bigbox-text
                    ul
                        li Vollintergriertes Monitoring-System für UHF, elektrische (IEC&nbsp60270), akustische und kapazitive TE-Messung
                        li Kontinuierliche, parallele Messung aller Kanäle
                        li Konfigurierbar mit bis zu 6 UHF, elektrischen, akustischen oder TEV TE-Kanälen
                        li Intergrierte Web-GUI mit PRPDs, Trends und Konfiguration
                        li ModbusTCP, IEC&nbsp61850, HTTP API und Relais integriert
                    div.cta.bigbox-link
                        Sitelink(to="/de/products/pdm-600", text="Mehr Information zum PDM-600")
                    
        .content-container
            hgroup
                h2 Passt jetzt zu mehr Ölventilen: Der verbesserte UHF-DVS-50
                p.subtitle UHF-TE-Sensor für die Nachrüstung an DN50 und DN80 Ölschiebern
            .columns
                picture.content-picture.bigbox-image
                    source(type='image/webp', srcset='@/assets/photos/UHF-DVS-50.webp')
                    img( src='@/assets/photos/UHF-DVS-50.png', width="100%", alt='UHF-TE-Sensor UHF-DVS-50', title='UHF-TE-Sensor UHF-DVS-50')
                div.bigbox-text
                    ul
                        li Neu: Mit vollständig zurückziehbarem Sensorkopf
                        li Für ölgefüllte Transformatoren
                        li Einschubtiefe max. 410 mm
                        li Bandbreite: 100 MHz ... 3 GHz
                        li Installation an vorhandenen DN50 und DN80 Schiebern
                        li Integrierter Hochpassfilter
                    div.cta.bigbox-link
                        Sitelink(to="/de/products/uhfsensors/uhf-dvs-50", text="Mehr Informationen zum UHF-DVS-50")

        .content-container(v-if='upcomingEvents.length')
            h2.secondary-header Kommende Veranstaltungen
        Event(
            v-for='event in upcomingEvents'
            :key='event.image'
            :image='event.image'
            :website='event.website'
            contact-path='/de/contact'
            contact-text='Einen Termin vereinbaren'
            v-bind='event.de'
        )


</template>

<script>
import Event from "@/components/Event.vue";

export default {
    components: {
        Event,
    },
    data() {
        return {
            events: [
                {
                    image: require("@/assets/logos/vde.svg"),
                    website: "https://www.vde-hochspannungstechnik.de/",
                    endDate: "2026-11-12",
                    en: {
                        eventTitle: "VDE Hochspannungstechnik 2026",
                        date: "10 - 12 November 2026",
                        location: "Berlin, Germany",
                    },
                    de: {
                        eventTitle: "VDE Hochspannungstechnik 2026",
                        date: "10. - 12. November 2026",
                        location: "Berlin, Deutschland",
                    },
                },
                {
                    image: require("@/assets/logos/PTDX26.webp"),
                    website: "https://www.powertranstech.com/",
                    endDate: "2026-11-19",
                    en: {
                        eventTitle: "Power Transmission & Distribution Technology Expo",
                        date: "17 - 19 November 2026",
                        location: "Köln, Germany",
                    },
                    de: {
                        eventTitle: "Power Transmission & Distribution Technology Expo",
                        date: "17. - 19. November 2026",
                        location: "Köln, Deutschland",
                    },
                },
                {
                    image: require("@/assets/logos/XII_WORKSPOT_2026.webp"),
                    website: "https://cigreworkspot.com.br/",
                    endDate: "2026-12-02",
                    en: {
                        eventTitle: "CIGRE XII WORKSPOT",
                        date: "30 November - 02 December 2026",
                        location: "Belo Horizonte, Brazil",
                    },
                    de: {
                        eventTitle: "CIGRE XII WORKSPOT",
                        date: "30. November - 02. Dezember 2026",
                        location: "Belo Horizonte, Brasilien",
                    },
                },
                {
                    image: require("@/assets/logos/EP2026.svg"),
                    website: "https://www.epchinashow.com/EP/idx/eng",
                    endDate: "2026-12-05",
                    en: {
                        eventTitle: "EP Shanghai",
                        date: "03 - 05 December 2026",
                        location: "Shanghai, China",
                    },
                    de: {
                        eventTitle: "EP Shanghai",
                        date: "03. - 05. Dezember 2026",
                        location: "Shanghai, China",
                    },
                },

            ],
        };
    },
    computed: {
        upcomingEvents() {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            return this.events
                .filter((event) => {
                    const endDate = new Date(`${event.endDate}T00:00:00`);
                    return today <= endDate;
                })
                .sort((firstEvent, secondEvent) =>
                    firstEvent.endDate.localeCompare(secondEvent.endDate)
                )
                .slice(0, 4);
        },
    },
    meta: {
        de: {
            title: "BSS Home",
            description:
                "Die Geschäftsfelder der BSS umfassen das Teilentladungs-Monitoring und die Teilentladungs-Diagnose von Transformatoren sowie die breitbandige Messung der Spannungsqualität in allen Spannungsebenen der Energieversorgung.",
        },
        en: {
            title: "BSS Home",
            description:
                "BSS is operating in the area of energy technology. Its area of expertise spans from the partial discharge monitoring and partial discharge diagnosis of power transformers to the broadband measurement and evaluation of power quality at all voltage levels.",
        },
    },
};
</script>

<style lang='sass' scoped>

.video-container
    position: relative
    width: 100%
    height: 780px
    background-color: white
    overflow: hidden
    line-height: 0

    @media (max-width: $mobile-breakpoint)
        height: 600px

.hero-video
    width: 100%
    height: 100%
    display: block
    object-fit: cover
    object-position: center

.welcome-box
    position: absolute
    bottom: 0
    left: 0
    right: 0
    width: 100%
    z-index: 2
    background-color: rgba(0, 92, 148, 0.5)
    backdrop-filter: blur(5px)
    -webkit-backdrop-filter: blur(5px)
    color: white
    padding: 30px 40px 35px;
    box-sizing: border-box
    text-align: center
    line-height: 1.5
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2)

    @media (max-width: $mobile-breakpoint)
        padding: 22px 16px
        font-size: 10px
    @media (max-width: 500px)
        font-size: 8px

    .welcome-content
        max-width: 980px
        margin: 0 auto

    h1
        font-size: 2.8em
        font-weight: 700
        color: white
        margin-top: 0
        margin-bottom: 8px
        line-height: 1.2
        letter-spacing: -0.5px
        @media (max-width: $mobile-breakpoint)
            margin-bottom: 6px

    .welcome-subslogan
        font-size: 4.6em
        font-weight: 600
        color: white
        margin-top: 0
        margin-bottom: 0
        letter-spacing: 0.3px
        line-height: 1.3

    .welcome-text
        font-size: 1.2em
        font-weight: 300
        line-height: 1.6
        color: rgba(255, 255, 255, 0.95)
        margin: 0

.content-container
    margin: 60px 40px

    @media (max-width: $mobile-breakpoint)
        margin: 40px 20px
        margin-bottom: 80px

.content-picture
    img
        max-height: 300px
        object-fit: contain

.columns
    display: flex
    ul
        li
            margin-bottom: 4px
    >div
        display: flex
        flex-direction: column
    @media (max-width: $mobile-breakpoint)
        flex-direction: column

.bigbox-image
    flex: 180px 1 1
    padding-right: 20px
.bigbox-text
    flex: 200px 1 1

.cta
    margin-top: 20px
    background-color: $color3
    padding: 4px 12px
    padding-right: 18px
    border-radius: 22px
    color: white
    float: right
    &.bigbox-link
        align-self: flex-start
        margin-left: 30px

hgroup
    h2
        margin-bottom: 0


.logo-area
    display: flex
    justify-content: space-between
    flex-wrap: wrap
    padding: 30px
    background-color: whitesmoke
    *
        max-width: 100px
        margin: 20px 10px


.columns
    div
        .secondary-header
            margin-bottom: 0.2em

.columns.flex-equal
    >div
        flex: 1

.solution-content
    margin-bottom: 0px
    .bigbox-link
        margin-left: 0

    .columns
        @media (max-width: 1000px)
            flex-direction: column

.solution-image
    margin: 40px auto
    display: flex
    align-items: center
    justify-content: center
    width: 100%

    img
        width: 80%
        max-width: 80%
        height: auto
        display: block
        margin: 0 auto

    @media (max-width: 1000px)
        margin: 20px 0
        img
            width: 100%
            max-width: 500px

.arrow-list
    list-style: none
    padding-left: 0
    margin: 12px 0

    li
        position: relative
        padding-left: 35px
        margin-bottom: 20px !important
        line-height: 1.5

        &::before
            content: ""
            position: absolute
            left: 0
            top: 0px
            width: 25px
            height: 25px
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%230084C4' stroke-width='3.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cline x1='4' y1='12' x2='20' y2='12'%3E%3C/line%3E%3Cpolyline points='13 5 20 12 13 19'%3E%3C/polyline%3E%3C/svg%3E")
            background-repeat: no-repeat
            background-position: center
            background-size: contain
</style>